import {
  AUTH_SKIP,
  CART_SERVICE_SKIP,
  EMPTY_CART_SKIP,
  ENTITLED_SKIP,
  FORMATION_PLAN_SKIP,
  NEVER_SKIP,
  NOT_NY_JURISDICTION,
  STARTER_PLAN_SKIP
} from '#client/utils/flowPageSkips';

/**
 * Page keys for the flow config.
 * This enum will provide autocomplete and type checking for the pageKeys array
 * @enum {string}
 */
export const PAGE_KEYS = Object.freeze({
  ADVANCED_COMPLIANCE: 'ADVANCED_COMPLIANCE',
  BANKING: 'BANKING',
  BASIC_COMPLIANCE: 'BASIC_COMPLIANCE',
  BOI: 'BOI',
  BUSINESS_EXPERIENCE: 'BUSINESS_EXPERIENCE',
  BUSINESS_INFO: 'BUSINESS_INFO',
  BUSINESS_INFO_BOI: 'BUSINESS_INFO_BOI',
  BUSINESS_INFO_CORA: 'BUSINESS_INFO_CORA',
  BUSINESS_LICENSE_REPORT: 'BUSINESS_LICENSE_REPORT',
  BUSINESS_STAGE: 'BUSINESS_STAGE',
  BUSINESS_WEBSITE: 'BUSINESS_WEBSITE', // for AI flow
  CHANGE_OF_REGISTERED_AGENT: 'CHANGE_OF_REGISTERED_AGENT',
  CHECKOUT: 'CHECKOUT',
  CHECKOUT_SMART_LLC: 'CHECKOUT_SMART_LLC',
  CONCLUSION: 'CONCLUSION',
  CONCLUSION_QUOTE: 'CONCLUSION_QUOTE',
  CONCLUSION_SMART_LLC: 'CONCLUSION_SMART_LLC',
  CONFIRMATION: 'CONFIRMATION',
  CONTACT_INFO: 'CONTACT_INFO',
  CORPORATE_BYLAWS: 'CORPORATE_BYLAWS',
  DOMAIN_NAME_REGISTRATION: 'DOMAIN_NAME_REGISTRATION',
  EIN: 'EIN',
  INDUSTRY: 'INDUSTRY',
  MONEY_PRO: 'MONEY_PRO',
  MULTI_STATE_REGISTERED_AGENT: 'MULTI_STATE_REGISTERED_AGENT',
  NEW_YORK_PUBLICATION: 'NEW_YORK_PUBLICATION',
  OPERATING_AGREEMENT: 'OPERATING_AGREEMENT',
  PACKAGE_SELECTION: 'PACKAGE_SELECTION',
  QUALIFICATION: 'QUALIFICATION',
  REGISTERED_AGENT: 'REGISTERED_AGENT',
  REGISTERED_AGENT_AND_WFC: 'REGISTERED_AGENT_AND_WFC',
  RUSH_FILING: 'RUSH_FILING',
  TAXES: 'TAXES',
  WEBSITE_BUILDER: 'WEBSITE_BUILDER',
  WFC: 'WFC',
  // For Mobile flows
  GUARANTEE: 'GUARANTEE',
  PRODUCTS_PHONE: 'PRODUCTS_PHONE',
  QUESTIONS: 'QUESTIONS'
});

/**
 * @typedef {object} Page
 * @property {string} path - The path of the page.
 * @property {string[]} services - The associated services required for the page. Used to track products viewed and plan skips.
 * @property {string[]} entitlements - The set of entitlements the page represents. Used to check entitled skips.
 * @property {import ("../utils/flowPageSkips").FlowPageSkip[]} skips - The skip functions for the page.
 * @property {string} pageComponent - The component to render for the page.
 * @property {object} [pageComponentProps] - Props to customize individual page components for a given flow. Configured in the flow config.
 */
const PAGE_CONFIG = {
  [PAGE_KEYS.ADVANCED_COMPLIANCE]: {
    path: 'advanced-compliance',
    services: [], // Fill this with the proper service
    entitlements: [],
    skips: [CART_SERVICE_SKIP],
    pageComponent: 'AdvancedCompliance'
  },
  [PAGE_KEYS.BANKING]: {
    path: 'banking',
    services: ['zb_banking_preferred'],
    entitlements: ['banking'],
    skips: [FORMATION_PLAN_SKIP, ENTITLED_SKIP],
    pageComponent: 'Banking'
  },
  [PAGE_KEYS.BASIC_COMPLIANCE]: {
    path: 'basic-compliance',
    services: [], // Fill this with the proper service
    entitlements: [],
    skips: [CART_SERVICE_SKIP],
    pageComponent: 'BasicCompliance'
  },
  [PAGE_KEYS.BOI]: {
    path: 'beneficial-ownership-information',
    services: ['beneficial_ownership_filing', 'ongoing_bof_compliance'],
    entitlements: ['beneficial_ownership_filing'],
    skips: [ENTITLED_SKIP],
    pageComponent: 'BeneficialOwnershipInformation'
  },
  [PAGE_KEYS.BUSINESS_EXPERIENCE]: {
    path: 'business-experience',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'BusinessExperience'
  },
  [PAGE_KEYS.BUSINESS_INFO]: {
    path: 'business-info',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'BusinessInfo'
  },
  [PAGE_KEYS.BUSINESS_INFO_BOI]: {
    path: 'business-info',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'BusinessInfoBOI'
  },
  [PAGE_KEYS.BUSINESS_INFO_CORA]: {
    path: 'business-info',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'BusinessInfoCORA'
  },
  [PAGE_KEYS.BUSINESS_LICENSE_REPORT]: {
    path: 'business-license-report',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'BusinessLicenseReport'
  },
  [PAGE_KEYS.BUSINESS_STAGE]: {
    path: 'business-stage',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'BusinessStage'
  },
  [PAGE_KEYS.BUSINESS_WEBSITE]: {
    path: 'business-website',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'BusinessWebsite'
  },
  [PAGE_KEYS.CHANGE_OF_REGISTERED_AGENT]: {
    path: 'change-of-registered-agent',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'ChangeOfRegisteredAgent'
  },
  [PAGE_KEYS.CHECKOUT]: {
    path: 'checkout',
    services: [],
    entitlements: [],
    skips: [EMPTY_CART_SKIP],
    pageComponent: 'Checkout'
  },
  [PAGE_KEYS.CHECKOUT_SMART_LLC]: {
    path: 'checkout',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'CheckoutSmartLlc'
  },
  [PAGE_KEYS.CONCLUSION]: {
    path: 'conclusion',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'Conclusion'
  },
  [PAGE_KEYS.CONCLUSION_QUOTE]: {
    path: 'conclusion',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'ConclusionQuote'
  },
  [PAGE_KEYS.CONCLUSION_SMART_LLC]: {
    path: 'conclusion',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'ConclusionSmartLlc'
  },
  [PAGE_KEYS.CONFIRMATION]: {
    path: 'confirmation',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'Confirmation'
  },
  [PAGE_KEYS.CONTACT_INFO]: {
    path: 'contact-info',
    services: [],
    entitlements: [],
    skips: [AUTH_SKIP],
    pageComponent: 'ContactInfo'
  },
  [PAGE_KEYS.CORPORATE_BYLAWS]: {
    path: 'corporate-bylaws',
    services: ['operating_agreement'],
    entitlements: ['operating_agreement'],
    skips: [FORMATION_PLAN_SKIP],
    pageComponent: 'CorporateBylaws'
  },
  [PAGE_KEYS.DOMAIN_NAME_REGISTRATION]: {
    path: 'domain-name',
    services: ['domain_name_reg'],
    entitlements: ['domain_name_reg'],
    skips: [FORMATION_PLAN_SKIP, ENTITLED_SKIP],
    pageComponent: 'DomainNameRegistration'
  },
  [PAGE_KEYS.EIN]: {
    path: 'employer-identification-number',
    services: ['corporate_docs', 'ein_creation', 'ein_business_docs_bundle'],
    entitlements: [],
    skips: [FORMATION_PLAN_SKIP],
    pageComponent: 'EIN'
  },
  [PAGE_KEYS.INDUSTRY]: {
    path: 'industry',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'Industry'
  },
  [PAGE_KEYS.MONEY_PRO]: {
    path: 'money-pro',
    services: ['zb_money_pro'],
    entitlements: ['money_pro'],
    skips: [FORMATION_PLAN_SKIP, ENTITLED_SKIP],
    pageComponent: 'MoneyPro'
  },
  [PAGE_KEYS.MULTI_STATE_REGISTERED_AGENT]: {
    path: 'multi-state-registered-agent',
    services: [], // TODO: This service is not in the services-api
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'MultiStateRegisteredAgent'
  },
  [PAGE_KEYS.NEW_YORK_PUBLICATION]: {
    path: 'ny-publication',
    services: ['ny_publication'],
    entitlements: [],
    skips: [NOT_NY_JURISDICTION, FORMATION_PLAN_SKIP],
    pageComponent: 'NewYorkPublication'
  },
  [PAGE_KEYS.OPERATING_AGREEMENT]: {
    path: 'operating-agreement',
    services: ['operating_agreement'],
    entitlements: ['operating_agreement'],
    skips: [FORMATION_PLAN_SKIP],
    pageComponent: 'OperatingAgreement'
  },
  [PAGE_KEYS.PACKAGE_SELECTION]: {
    path: 'package-selection',
    services: ['starter_plan_v36', 'pro_plan_v36', 'premium_plan_v36'],
    entitlements: ['formation_llc', 'formation_corp'],
    skips: [FORMATION_PLAN_SKIP],
    pageComponent: 'PlanSelection' // TODO: Make this naming convention consistent with config name
  },
  [PAGE_KEYS.QUALIFICATION]: {
    path: 'qualification',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'Qualification'
  },
  [PAGE_KEYS.REGISTERED_AGENT]: {
    path: 'registered-agent',
    services: ['registered_agent_service'],
    entitlements: [],
    skips: [FORMATION_PLAN_SKIP],
    pageComponent: 'RegisteredAgent'
  },
  [PAGE_KEYS.REGISTERED_AGENT_AND_WFC]: {
    path: 'registered-agent',
    services: ['ra_starter_plan_v3', 'ra_pro_plan_v3'],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'RegisteredAgentAndWFC'
  },
  [PAGE_KEYS.RUSH_FILING]: {
    path: 'rush-filing',
    services: ['rush'],
    entitlements: [],
    skips: [FORMATION_PLAN_SKIP],
    pageComponent: 'RushFiling'
  },
  [PAGE_KEYS.TAXES]: {
    path: 'taxes',
    services: ['zb_tax_advice_biztax_prep'],
    entitlements: [],
    skips: [FORMATION_PLAN_SKIP, STARTER_PLAN_SKIP],
    pageComponent: 'Taxes'
  },
  [PAGE_KEYS.WEBSITE_BUILDER]: {
    path: 'website-builder',
    services: ['static_website'],
    entitlements: ['static_website'],
    skips: [FORMATION_PLAN_SKIP, ENTITLED_SKIP],
    pageComponent: 'WebsiteBuilder'
  },
  [PAGE_KEYS.WFC]: {
    path: 'worry-free-compliance',
    services: ['worry_free_service'],
    entitlements: ['worry_free_service'],
    skips: [FORMATION_PLAN_SKIP],
    pageComponent: 'WFC'
  },
  // For Mobile flows
  [PAGE_KEYS.GUARANTEE]: {
    path: 'guarantee',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'Guarantee'
  },
  [PAGE_KEYS.PRODUCTS_PHONE]: {
    path: 'products',
    services: [], // ?
    entitlements: [], // ?
    skips: [NEVER_SKIP],
    pageComponent: 'ProductsPhone'
  },
  [PAGE_KEYS.QUESTIONS]: {
    path: 'questions',
    services: [],
    entitlements: [],
    skips: [NEVER_SKIP],
    pageComponent: 'Questions'
  }
};

export default PAGE_CONFIG;
